import { useEffect, useRef, useState } from 'react';
import { PHOTO_KIND_LABELS, type PhotoKind } from '../api.ts';
import { prepareImage, type PreparedImage } from '../lib/image.ts';
import { Icon } from './Icon.tsx';

export type PendingPhoto = {
  key: string;
  kind: PhotoKind;
  prepared: PreparedImage;
  previewUrl: string;
};

/**
 * Выбор снимков в форме создания образца.
 *
 * Образца ещё нет, значит и загружать некуда: снимки готовятся на клиенте
 * (EXIF, поворот, сжатие) и ждут здесь, пока образец не сохранится. Это
 * первый шаг занесения по ТЗ 4.3 — фото и название минерала, остальное
 * потом.
 */
export function PhotoPicker({
  photos,
  onChange,
}: {
  photos: PendingPhoto[];
  onChange: (photos: PendingPhoto[]) => void;
}) {
  const cameraInput = useRef<HTMLInputElement>(null);
  const galleryInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Ссылки на превью живут до размонтирования: иначе браузер держит
  // объекты в памяти до перезагрузки страницы.
  useEffect(
    () => () => {
      for (const photo of photos) URL.revokeObjectURL(photo.previewUrl);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  async function pick(files: FileList | null, kind: PhotoKind) {
    if (!files?.length) return;
    setError(null);
    const added: PendingPhoto[] = [];

    for (const [index, file] of Array.from(files).entries()) {
      try {
        setBusy(files.length > 1 ? `Готовим ${index + 1} из ${files.length}…` : 'Готовим снимок…');
        const prepared = await prepareImage(file);
        added.push({
          key: `${Date.now()}-${index}`,
          kind,
          prepared,
          previewUrl: URL.createObjectURL(prepared.thumb),
        });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Не удалось подготовить снимок');
        break;
      }
    }

    setBusy(null);
    if (added.length) onChange([...photos, ...added]);
  }

  function remove(key: string) {
    const photo = photos.find((p) => p.key === key);
    if (photo) URL.revokeObjectURL(photo.previewUrl);
    onChange(photos.filter((p) => p.key !== key));
  }

  function setKind(key: string, kind: PhotoKind) {
    onChange(photos.map((p) => (p.key === key ? { ...p, kind } : p)));
  }

  return (
    <>
      <div className="photo-row">
        <button
          type="button"
          className="well well--dashed"
          onClick={() => cameraInput.current?.click()}
          disabled={busy !== null}
        >
          <Icon name="camera" className="icon" />
          <span className="hint">снять</span>
        </button>
        <button
          type="button"
          className="well well--dashed"
          onClick={() => galleryInput.current?.click()}
          disabled={busy !== null}
        >
          <Icon name="photo" className="icon" />
          <span className="hint">из галереи</span>
        </button>

        {photos.map((photo) => (
          <div key={photo.key} className="pending-photo">
            <img src={photo.previewUrl} alt="" />
            <button
              type="button"
              className="pending-photo-remove"
              aria-label="Убрать снимок"
              onClick={() => remove(photo.key)}
            >
              <Icon name="x" className="icon icon-xs" />
            </button>
            <select
              className="pending-photo-kind"
              value={photo.kind}
              aria-label="Тип снимка"
              onChange={(e) => setKind(photo.key, e.target.value as PhotoKind)}
            >
              {(Object.keys(PHOTO_KIND_LABELS) as PhotoKind[]).map((value) => (
                <option key={value} value={value}>
                  {PHOTO_KIND_LABELS[value]}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>

      <input
        ref={cameraInput}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => {
          void pick(e.target.files, 'specimen');
          e.target.value = '';
        }}
      />
      <input
        ref={galleryInput}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => {
          void pick(e.target.files, 'specimen');
          e.target.value = '';
        }}
      />

      {busy && <div className="hint">{busy}</div>}
      {photos.length > 0 && !busy && (
        <div className="hint">
          {photos.length === 1 ? 'Снимок загрузится' : `Снимков загрузится: ${photos.length}`} после
          сохранения образца
        </div>
      )}
      {error && (
        <div className="notice notice--danger" style={{ marginTop: 'var(--space-3)' }}>
          {error}
        </div>
      )}
    </>
  );
}
